import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2 as ConnectedIcon,
  AlertCircle as ErrorIcon,
  Settings as ConfigIcon,
  Zap as TestIcon,
  RefreshCw as RefreshIcon,
  GripVertical as DragIcon,
} from 'lucide-react';
import {
  Alert,
  Heading,
  BodyText,
  Button,
  Card,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
  Skeleton,
  Badge,
  Tabs,
  type TabItem,
  Input,
  Dialog,
  Switch,
  TableScroll,
} from '@aaimplatform/ui';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useThirdPartyIntegrations, useTestIntegration, useToggleIntegration } from '../hooks';
import type { Integration, IntegrationTestResult } from '../api/client';
import type { components as PlatformAdminComponents } from '@aaimplatform/institutional-types/platform-admin';
import { providerCredentialApi } from '../api/client';
import { INTEGRATION_STATUS_BADGE } from './platform-config/integration-status';
import { ABSENT, formatCount } from '../utils/display-format';
import { enumLabel } from '../utils/status-labels';
import { readFailureDetail } from '../lib/read-failure';

// 2026-04-29 (Sprint C11 Lane 5): subcategory, version, isAaimProduct, and
// vendor are now first-class fields on the canonical platform-admin Integration
// schema (schema-registry/platform-admin.openapi.yaml), so the generated
// `Integration` type already includes them. The local extension that previously
// patched these in is no longer needed; alias for call-site readability only.
type IntegrationUI = Integration;

type IntegrationCategory = PlatformAdminComponents['schemas']['IntegrationCategory'];

// Sprint C17 (2026-05-04): page now shows third-party integrations only.
// Aaim internal services moved to the Aaim Services page (route /aaim-services)
// under the System navgroup. The AAIM tab is removed accordingly.
//
// The category tabs are keyed by the generated IntegrationCategory union
// (platform-admin.openapi.yaml), so a category the contract adds is a tsc error
// here until it has a label. The hand-written list this replaced carried SSO,
// RISK and CREDIT tabs for categories the contract does not define; they
// matched no row and always showed "No integrations found" (op-g2-7).

/** Tab labels, one per contract category, in display order. */
export const INTEGRATION_CATEGORY_TAB_LABEL: Record<IntegrationCategory, string> = {
  DIRECT_INTEGRATION: 'Direct Integrations',
  LOS: 'Loan Origination',
  CUSTODY: 'Custody Providers',
  TRANSFER_AGENT: 'Transfer Agents',
  MARKET_DATA: 'Market Data',
  COMPLIANCE: 'Compliance',
};

/** The category chip on a row. */
export const INTEGRATION_CATEGORY_LABEL: Record<IntegrationCategory, string> = {
  DIRECT_INTEGRATION: 'Direct integration',
  LOS: 'Loan origination',
  CUSTODY: 'Custody',
  TRANSFER_AGENT: 'Transfer agent',
  MARKET_DATA: 'Market data',
  COMPLIANCE: 'Compliance',
};

export const INTEGRATION_CATEGORY_TABS: TabItem[] = [
  { id: 'ALL', label: 'All Integrations' },
  ...(Object.keys(INTEGRATION_CATEGORY_TAB_LABEL) as IntegrationCategory[]).map((id) => ({
    id,
    label: INTEGRATION_CATEGORY_TAB_LABEL[id],
  })),
];

const HEADER_CELL_CLASS =
  'py-2.5 px-4 text-label-xs font-sans font-semibold uppercase tracking-widest text-tertiary';

/** Columns in the integrations table, for the full-width message rows. */
const TABLE_COLUMN_COUNT = 7;

/**
 * The provider credential an integration's API key is stored under: the first
 * word of its vendor (or name), upper-cased. provider-credentials is keyed by
 * provider and Integration declares no provider key, so two rows from one
 * vendor (Coinbase Prime and Coinbase retail) read and write one credential
 * (op-g2-2). The Configure dialog names the credential and the rows sharing it.
 */
export function credentialProviderName(integration: Pick<IntegrationUI, 'vendor' | 'name'>): string {
  return (integration.vendor ?? integration.name ?? '').trim().toUpperCase().split(/\s+/)[0] ?? '';
}

/** The other third-party rows whose key is stored under the same credential. */
export function integrationsSharingCredential(
  all: readonly IntegrationUI[],
  integration: IntegrationUI,
): IntegrationUI[] {
  const provider = credentialProviderName(integration);
  if (!provider) return [];
  return all.filter(
    (other) =>
      other.id !== integration.id && !other.isAaimProduct && credentialProviderName(other) === provider,
  );
}

/** A row connection test, as the page reports it beside the table. */
export interface RowTestResultState {
  type: 'success' | 'warning' | 'error';
  message: string;
}

/**
 * Words for a row connection test. The row action discarded the answer, so a
 * test of a row with no test endpoint (every third-party row but ATTOM) had no
 * visible effect (op-g2-3). An unsuccessful answer says no connection was
 * verified and prints the server's reason.
 */
export function describeRowTest(
  name: string,
  result: Pick<IntegrationTestResult, 'success' | 'latencyMs' | 'error'>,
): RowTestResultState {
  if (result.success) {
    return {
      type: 'success',
      message: `${name}: connection healthy (${formatCount(result.latencyMs)} ms).`,
    };
  }
  return {
    type: 'warning',
    message: `${name}: no connection was verified. Server response: ${readFailureDetail(result.error ?? '')}.`,
  };
}

interface SortableRowProps {
  integration: IntegrationUI;
  getStatusIcon: (status: string | undefined) => JSX.Element;
  handleToggle: (id: string, newEnabled: boolean) => void;
  handleTest: (id: string) => void;
  testing: string | null;
  onConfigure: (integration: IntegrationUI) => void;
}

function SortableRow({ integration, getStatusIcon, handleToggle, handleTest, testing, onConfigure }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: integration.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const status = integration.status ?? 'NOT_CONFIGURED';
  const badge = INTEGRATION_STATUS_BADGE[status];

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-recessed border-b border-recessed">
      <td
        className="py-3 px-4 w-10 cursor-grab"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${integration.name}`}
      >
        <DragIcon size={20} className="text-tertiary" aria-hidden />
      </td>
      <td className="py-3 px-4">
        <div className="flex items-start gap-2">
          {/* F-PA-INT-ICON-001: fixed-width, non-shrinking icon container.
              Long descriptions that wrap to 2 lines previously squeezed the
              SVG below 20px on rows like Coinbase Retail and Drata. */}
          <div className="flex-shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center">
            {getStatusIcon(status)}
          </div>
          <div className="min-w-0">
            <BodyText variant="ui" className="font-medium text-sm">{integration.name}</BodyText>
            <BodyText variant="ui" className="text-tertiary text-xs">{integration.description ?? ''}</BodyText>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1">
          <span
            className="inline-block px-2 py-1 rounded text-xs border border-recessed text-secondary whitespace-nowrap"
            title={integration.category}
          >
            {enumLabel(INTEGRATION_CATEGORY_LABEL, integration.category)}
          </span>
          {integration.subcategory && (
            <span className="inline-block px-2 py-1 rounded text-xs bg-elevated text-secondary">
              {integration.subcategory}
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="whitespace-nowrap">
          <Badge variant={badge.variant} size="small">
            {badge.label}
          </Badge>
        </span>
      </td>
      <td className="py-3 px-4 text-right">
        {/* An absent count prints "--"; a stored count prints as stored. */}
        <span className="font-mono text-sm text-primary tabular-nums">{formatCount(integration.tenantsUsing)}</span>
      </td>
      <td className="py-3 px-4">
        <Switch
          isSelected={integration.enabled}
          isDisabled={status === 'NOT_CONFIGURED'}
          onChange={(val) => handleToggle(integration.id, val)}
          aria-label={
            status === 'NOT_CONFIGURED'
              ? `Toggle disabled until ${integration.name} is configured`
              : `Toggle ${integration.name} integration`
          }
        />
      </td>
      <td className="py-3 px-4 text-right">
        <div className="flex gap-1 justify-end">
          <button
            type="button"
            onClick={() => onConfigure(integration)}
            className="p-1 hover:bg-recessed rounded transition-colors"
            title="Configure"
            aria-label={`Configure ${integration.name}`}
          >
            <ConfigIcon size={20} className="text-secondary" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => handleTest(integration.id)}
            disabled={testing === integration.id || status === 'NOT_CONFIGURED'}
            className="p-1 hover:bg-recessed rounded transition-colors disabled:opacity-50"
            title="Test Connection"
            aria-label={`Test connection for ${integration.name}`}
          >
            <TestIcon size={20} className="text-secondary" aria-hidden />
          </button>
        </div>
      </td>
    </tr>
  );
}

/** The stored key for the Configure dialog: loading, read (masked), or not read. */
type StoredKeyState =
  | { state: 'loading' }
  | { state: 'stored'; maskedKey: string }
  | { state: 'unread'; detail: string };

export interface ProviderKeyFieldsProps {
  integration: IntegrationUI;
  /** Other rows whose key is stored under the same provider credential. */
  sharing: readonly IntegrationUI[];
  storedKey: StoredKeyState;
  newApiKey: string;
  onNewApiKeyChange: (value: string) => void;
}

/**
 * The key section of the Configure dialog. The stored key prints masked beside
 * the input, and the input takes a new key only, masked as typed: it showed the
 * masked stored key as its value in a plain text field (op-g2-2).
 */
export function ProviderKeyFields({
  integration,
  sharing,
  storedKey,
  newApiKey,
  onNewApiKeyChange,
}: ProviderKeyFieldsProps) {
  const provider = credentialProviderName(integration);
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <dt className="text-tertiary text-sm">Stored as</dt>
          <dd className="font-mono text-sm text-primary">{provider || ABSENT}</dd>
        </div>
        <div>
          <dt className="text-tertiary text-sm">Current key</dt>
          <dd className="text-sm text-primary">
            {storedKey.state === 'loading' && 'Reading the stored key...'}
            {storedKey.state === 'stored' && <span className="font-mono">{storedKey.maskedKey}</span>}
            {storedKey.state === 'unread' && `No stored key was read. Server response: ${storedKey.detail}`}
          </dd>
        </div>
      </dl>
      {sharing.length > 0 && (
        <BodyText variant="ui" className="text-secondary text-sm">
          {`This key is also the key for ${sharing.map((i) => i.name).join(', ')}. Saving it changes the key for ${sharing.length === 1 ? 'that integration' : 'those integrations'} too.`}
        </BodyText>
      )}
      <div>
        <label htmlFor="integration-api-key" className="block text-sm font-medium text-primary mb-1">
          New API key
        </label>
        <Input
          id="integration-api-key"
          autoComplete="off"
          type="password"
          placeholder="Enter a new API key"
          value={newApiKey}
          onChange={(e) => onNewApiKeyChange(e.target.value)}
        />
      </div>
    </div>
  );
}

export function IntegrationsPage() {
  // Fetch integrations from API
  const { data: integrationsData, isLoading, isFetching, error, refetch } = useThirdPartyIntegrations();
  const testIntegration = useTestIntegration();
  const toggleIntegration = useToggleIntegration();

  // Local state for drag-and-drop reordering and toggle state
  const [integrations, setIntegrations] = useState<IntegrationUI[]>([]);
  const [activeTab, setActiveTab] = useState('ALL');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationUI | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [rowTestResult, setRowTestResult] = useState<RowTestResultState | null>(null);

  // Dialog state. The dialog edits the provider API key only. It also offered
  // API Endpoint, API Secret and Webhook URL, which it reset to blank on every
  // open and saved over the stored config, and which no service reads; the
  // secret was stored as plain JSON outside the encrypted credential store
  // (op-g2-2).
  const [newApiKey, setNewApiKey] = useState('');
  const [storedKey, setStoredKey] = useState<StoredKeyState>({ state: 'loading' });
  const [configTestResult, setConfigTestResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [configTesting, setConfigTesting] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  // Sync API data to local state
  useEffect(() => {
    if (integrationsData) {
      setIntegrations(integrationsData as IntegrationUI[]);
    }
  }, [integrationsData]);

  // Load the stored (masked) key when the dialog opens. A failed read says so;
  // it no longer passes silently.
  useEffect(() => {
    if (!configDialogOpen || !selectedIntegration) return;
    setConfigTestResult(null);
    setNewApiKey('');
    if (selectedIntegration.isAaimProduct) return;
    let stale = false;
    setStoredKey({ state: 'loading' });
    providerCredentialApi.getCredential(credentialProviderName(selectedIntegration))
      .then((cred) => {
        if (!stale) setStoredKey({ state: 'stored', maskedKey: cred.maskedKey });
      })
      .catch((err: unknown) => {
        if (!stale) setStoredKey({ state: 'unread', detail: readFailureDetail(err) });
      });
    return () => {
      stale = true;
    };
  }, [configDialogOpen, selectedIntegration?.id]);

  const handleDialogTest = useCallback(async () => {
    if (!selectedIntegration) return;
    setConfigTesting(true);
    setConfigTestResult(null);
    try {
      const result = await testIntegration.mutateAsync(selectedIntegration.id);
      if (result.success) {
        setConfigTestResult({
          type: 'success',
          message: `Connection healthy${result.latencyMs ? ` (${result.latencyMs}ms)` : ''}.`,
        });
      } else {
        setConfigTestResult({
          type: 'error',
          message: result.error ?? 'Connection test failed.',
        });
      }
      refetch();
    } catch {
      setConfigTestResult({ type: 'error', message: 'Connection test failed. Check network connectivity.' });
    } finally {
      setConfigTesting(false);
    }
  }, [selectedIntegration, testIntegration, refetch]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setIntegrations((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Sprint C17: page is now third-party only. The AAIM filter branch is
  // retained as a no-op fallback in case the data ever surfaces an Aaim product
  // (it should not -- backend filters via isAaimProduct=false).
  const filteredIntegrations = activeTab === 'ALL'
    ? integrations
    : integrations.filter(i => i.category === activeTab);

  const getStatusIcon = (status: string | undefined) => {
    if (status === 'CONNECTED') return <ConnectedIcon className="text-growth" size={20} />;
    if (status === 'ERROR') return <ErrorIcon className="text-error" size={20} />;
    return <ConfigIcon className="text-tertiary" size={20} />;
  };

  const handleTest = async (id: string) => {
    const name = integrations.find((i) => i.id === id)?.name ?? 'Integration';
    setTesting(id);
    setRowTestResult(null);
    try {
      const result = await testIntegration.mutateAsync(id);
      setRowTestResult(describeRowTest(name, result));
      refetch();
    } catch (err) {
      setRowTestResult({
        type: 'error',
        message: `${name}: the connection test did not complete. Server response: ${readFailureDetail(err)}.`,
      });
    } finally {
      setTesting(null);
    }
  };

  const handleSaveApiKey = async () => {
    if (!selectedIntegration || selectedIntegration.isAaimProduct) return;
    const apiKey = newApiKey.trim();
    if (!apiKey) return;
    setConfigSaving(true);
    try {
      await providerCredentialApi.updateCredential(credentialProviderName(selectedIntegration), apiKey);
      setConfigDialogOpen(false);
      refetch();
    } catch (err) {
      setConfigTestResult({
        type: 'error',
        message: `The API key was not saved. Server response: ${readFailureDetail(err)}.`,
      });
    } finally {
      setConfigSaving(false);
    }
  };

  const handleToggle = (id: string, newEnabled: boolean) => {
    // Defense in depth: refuse to enable an integration whose status is
    // NOT_CONFIGURED. The Switch is disabled in that state, but a forced
    // change event (e.g., devtools, malformed table re-render) would
    // otherwise reach the API and fail. Server-side guard rejects with
    // 400 INVALID_STATE per integrations.routes.ts.
    const target = integrations.find((i) => i.id === id);
    if (target && newEnabled && (target.status ?? 'NOT_CONFIGURED') === 'NOT_CONFIGURED') {
      return;
    }
    // Optimistic local update
    setIntegrations(prev => prev.map(i =>
      i.id === id ? { ...i, enabled: newEnabled } : i
    ));
    // Persist to backend
    toggleIntegration.mutate(
      { id, enabled: newEnabled },
      {
        onError: () => {
          // Revert on error
          setIntegrations(prev => prev.map(i =>
            i.id === id ? { ...i, enabled: !newEnabled } : i
          ));
        },
      }
    );
  };

  const stats = {
    total: integrations.length,
    connected: integrations.filter(i => i.status === 'CONNECTED').length,
    configured: integrations.filter(i => i.status === 'CONFIGURED').length,
    notConfigured: integrations.filter(i => i.status === 'NOT_CONFIGURED').length,
  };

  return (
    <div>
      {/* Reload re-reads the stored statuses. It runs no connection test; the
          per-row test action does that. */}
      <PageHeader
        description="Third-party integrations: data providers, custodians, FI SSO and compliance evidence"
        actions={
          <Button
            variant="secondary"
            icon={RefreshIcon}
            iconPosition="left"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Reload
          </Button>
        }
      />

      {error && (
        <Alert type="error" title="Integrations could not be read" className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span>{error.message}</span>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-3 w-24 mb-3" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))
        ) : error ? (
          <>
            <MetricCard label="Total Integrations" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Connected" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Configured" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Not Configured" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard label="Total Integrations" value={formatCount(stats.total)} />
            <MetricCard label="Connected" value={formatCount(stats.connected)} />
            <MetricCard label="Configured" value={formatCount(stats.configured)} />
            <MetricCard label="Not Configured" value={formatCount(stats.notConfigured)} />
          </>
        )}
      </div>

      {/* Bare, as on every other tabbed page; the Card box around the strip is gone (op-g2-11). */}
      <Tabs
        aria-label="Integration category"
        tabs={INTEGRATION_CATEGORY_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="mb-4"
      />

      {rowTestResult && (
        <Alert type={rowTestResult.type} className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{rowTestResult.message}</span>
            <Button variant="ghost" size="sm" onClick={() => setRowTestResult(null)}>
              Dismiss
            </Button>
          </div>
        </Alert>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <Card>
          <TableScroll label="Integrations">
            <table className="w-full">
              <thead>
                <tr className="border-b border-recessed bg-elevated/50">
                  <th scope="col" className="py-2.5 px-4 w-10">
                    <span className="sr-only">Reorder</span>
                  </th>
                  <th scope="col" className={`${HEADER_CELL_CLASS} text-left`}>Integration</th>
                  <th scope="col" className={`${HEADER_CELL_CLASS} text-left`}>Category</th>
                  <th scope="col" className={`${HEADER_CELL_CLASS} text-left`}>Status</th>
                  {/* No Version column: Integration.version holds seeded literals
                      (Abrigo 2.3.1, Experian 4.2) on rows that never connected,
                      and no connection or test path writes it (op-g2-4). */}
                  <th scope="col" className={`${HEADER_CELL_CLASS} text-right`}>Tenants</th>
                  <th scope="col" className={`${HEADER_CELL_CLASS} text-left`}>Enabled</th>
                  <th scope="col" className={`${HEADER_CELL_CLASS} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={TABLE_COLUMN_COUNT} className="py-8 text-center text-tertiary">
                      Loading integrations...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={TABLE_COLUMN_COUNT} className="py-8 text-center text-tertiary">
                      Integrations could not be read
                    </td>
                  </tr>
                ) : filteredIntegrations.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLUMN_COUNT} className="py-8 text-center text-tertiary">
                      No integrations found
                    </td>
                  </tr>
                ) : (
                  <SortableContext items={filteredIntegrations.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {filteredIntegrations.map((integration) => (
                      <SortableRow
                        key={integration.id}
                        integration={integration}
                        getStatusIcon={getStatusIcon}
                        handleToggle={handleToggle}
                        handleTest={handleTest}
                        testing={testing}
                        onConfigure={(i) => { setSelectedIntegration(i); setConfigDialogOpen(true); }}
                      />
                    ))}
                  </SortableContext>
                )}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      </DndContext>

      {/* Config Dialog */}
      <Dialog
        open={configDialogOpen && !!selectedIntegration}
        onOpenChange={(open) => { if (!open) setConfigDialogOpen(false); }}
        title={`Configure: ${selectedIntegration?.name ?? ''}`}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
            <Button variant="secondary" onClick={handleDialogTest} disabled={configTesting}>
              {configTesting ? 'Testing...' : 'Test Connection'}
            </Button>
            {selectedIntegration && !selectedIntegration.isAaimProduct && (
              <Button
                variant="primary"
                disabled={configSaving || !newApiKey.trim()}
                onClick={handleSaveApiKey}
              >
                {configSaving ? 'Saving...' : 'Save API Key'}
              </Button>
            )}
          </>
        }
      >
        {selectedIntegration && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <BodyText variant="ui" className="text-tertiary text-sm">Vendor</BodyText>
                <BodyText variant="ui" className="font-medium">{selectedIntegration.vendor ?? 'Not specified'}</BodyText>
              </div>
              <div className="sm:col-span-2">
                <BodyText variant="ui" className="text-tertiary text-sm">Description</BodyText>
                <BodyText variant="ui">{selectedIntegration.description ?? 'No description available'}</BodyText>
              </div>
            </div>

            <hr className="my-6 border-recessed" />

            <Heading level={3} size="card" className="mb-4">API Key</Heading>
            {selectedIntegration.isAaimProduct ? (
              <BodyText variant="ui" className="text-secondary text-sm">
                Aaim services carry no provider API key.
              </BodyText>
            ) : (
              <ProviderKeyFields
                integration={selectedIntegration}
                sharing={integrationsSharingCredential(integrations, selectedIntegration)}
                storedKey={storedKey}
                newApiKey={newApiKey}
                onNewApiKeyChange={setNewApiKey}
              />
            )}

            {configTestResult && (
              <Alert type={configTestResult.type} className="mt-4">
                {configTestResult.message}
              </Alert>
            )}

            {/* Informational, not an alert: a neutral hairline, not the accent red. */}
            <div className="p-4 mt-4 rounded-sm bg-accent/5 border border-recessed">
              <BodyText variant="ui" className="text-secondary text-sm">
                Changes will apply to all tenants using this integration.
              </BodyText>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}
